/**
 * <IdleTimeoutGuard> — mounts the idle timer and its warning modal.
 *
 * This existed five times, once per app: main, portal, admin, account and
 * vendor. Diffed against each other the bodies were identical apart from the
 * app's default timeout, its product label, and which query supplies the
 * tenant override. Session expiry is not a good thing to have five copies of
 * — a fix or a policy change lands in one and silently misses the rest.
 *
 * What each app still owns is what it must: its auth provider, its own
 * GraphQL query (codegen requires a unique operation name per app), and its
 * default. Those arrive as props; the resolution order and the wiring live
 * here.
 *
 * Precedence, from `resolveIdleMinutes`: a JIT-elevated session is strictest,
 * then a tenant override, then the app default.
 */
import { useMemo, type ReactNode } from 'react';

import {
  IdleTimeoutProvider,
  resolveIdleMinutes,
  getAccessToken,
  type IdleLogoutFailure,
  type SignOutOptions,
} from '@clearpath/auth-client';
import { IdleWarningModal } from '@clearpath/auth-ui';

export interface IdleTimeoutGuardProps {
  children: ReactNode;
  /** Fallback when there is no tenant override. Per-surface: admin runs the
   *  tightest at 30 min, the customer portal the loosest. */
  defaultMinutes: number;
  /** Shown in the warning modal — "ClearPath", "ClearPath Vendor", … */
  productLabel: string;
  /** Whether a session exists; the timer is inert without one. */
  enabled: boolean;
  /** `tenantSettings.idleTimeoutMinutes`, or null when absent or errored. */
  tenantOverrideMinutes?: number | null;
  /** JIT-elevated sessions expire soonest regardless of the other two. */
  isJitGrant?: boolean;
  /** Sign-out callback forwarded to IdleTimeoutProvider unchanged so its
   *  structured sign-out result remains observable. It receives SignOutOptions
   *  (`trigger: "idle"` from the timer, `"user"` from "Sign out now", plus a
   *  pre-navigation failure hook), so pass the app's `signOut` directly. The
   *  `unknown` return accepts value-returning signOut callbacks. */
  onLogout: (options?: SignOutOptions) => unknown;
  /** Optional application telemetry sink for sanitized idle-guard sign-out failures. */
  onLogoutFailure?: (failure: IdleLogoutFailure) => void;
  /**
   * Refresh on "stay signed in". Defaults to the module-level token helper;
   * the portal overrides it because it holds its own auth-client instance.
   */
  onStay?: () => Promise<unknown>;
}

export function IdleTimeoutGuard({
  children,
  defaultMinutes,
  productLabel,
  enabled,
  tenantOverrideMinutes = null,
  isJitGrant = false,
  onLogout,
  onLogoutFailure,
  onStay,
}: IdleTimeoutGuardProps) {
  const idleMinutes = useMemo(
    () =>
      resolveIdleMinutes({
        appDefaultMinutes: defaultMinutes,
        tenantOverrideMinutes,
        isJitGrant,
      }),
    [defaultMinutes, tenantOverrideMinutes, isJitGrant],
  );

  return (
    <IdleTimeoutProvider
      idleMinutes={idleMinutes}
      enabled={enabled}
      onLogout={onLogout}
      onLogoutFailure={onLogoutFailure}
      // "Stay signed in" refreshes the token rather than only resetting the
      // timer — otherwise the session can expire server-side while the client
      // still believes it is alive. A failure here is not surfaced: the timer
      // keeps running and the next expiry logs out normally.
      onStay={async () => {
        try {
          await (onStay ? onStay() : getAccessToken());
        } catch {
          /* refresh failed; the timer still governs */
        }
      }}
    >
      {children}
      <IdleWarningModal productLabel={productLabel} />
    </IdleTimeoutProvider>
  );
}
